/** File category / sub-category labels from CMS data dictionary (Configure Assignment). */
export const FILE_CATEGORY_CIVIL_LITIGATION_ORIGINAL_COURTS =
  'Civil Division - Litigation - Original Courts';
export const FILE_CATEGORY_CIVIL_ADVISE = 'Civil Division - Advise';
export const SUB_FILE_CATEGORY_C_INSTITUTION_OF_ACTIONS = 'C - Institution of Actions';

export type ConfigureAssignmentFieldVisibility = {
  showSupervisor: boolean;
  showStateAttorney: boolean;
  showCounsel: boolean;
};

/** Normalize labels for comparison (trim, collapse spaces, unify dash variants). */
export function normalizeCategoryLabel(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[\u2013\u2014]/g, '-')
    .toLowerCase();
}

/** Civil Division - Litigation - Original Courts (combined or file + sub category). */
export function isCivilLitigationOriginalCourtsCategory(
  fileCategoryName: string,
  subFileCategoryName = '',
): boolean {
  const target = normalizeCategoryLabel(FILE_CATEGORY_CIVIL_LITIGATION_ORIGINAL_COURTS);
  const file = normalizeCategoryLabel(fileCategoryName);
  if (file === target) return true;
  const sub = normalizeCategoryLabel(subFileCategoryName);
  if (!sub) return false;
  const combined = normalizeCategoryLabel(
    `${fileCategoryName.trim()} - ${subFileCategoryName.trim()}`,
  );
  return combined === target;
}

function isCivilAdviseInstitutionOfActions(
  fileCategoryName: string,
  subFileCategoryName: string,
): boolean {
  return (
    normalizeCategoryLabel(fileCategoryName) === normalizeCategoryLabel(FILE_CATEGORY_CIVIL_ADVISE) &&
    normalizeCategoryLabel(subFileCategoryName) ===
      normalizeCategoryLabel(SUB_FILE_CATEGORY_C_INSTITUTION_OF_ACTIONS)
  );
}

/**
 * Field visibility for Configure Assignment modal (Assign Tasks + Escalation Tasks).
 * All three assignment roles are always shown and required.
 */
export function resolveConfigureAssignmentFieldVisibility(
  _fileCategoryName: string,
  _subFileCategoryName: string,
): ConfigureAssignmentFieldVisibility {
  return {
    showSupervisor: true,
    showStateAttorney: true,
    showCounsel: true,
  };
}

export function isStateAttorneyRole(roles: string): boolean {
  return /\bstate\s+attorney\b/i.test(roles.trim());
}

/** Counsel-type roles from user categorisation (excludes State Attorney). */
export function isCounselRole(roles: string): boolean {
  const r = roles.trim();
  if (!r || isStateAttorneyRole(r)) return false;
  return /\bcounsel\b/i.test(r);
}

/** Allocation Officer roles from user categorisation (may self-assign via Counsel dropdown). */
export function isAllocationOfficerRole(roles: string): boolean {
  const r = roles.trim();
  if (!r) return false;
  return /\ballocation\b/i.test(r) || /\ballocating\b/i.test(r);
}

/** Users eligible for the Configure Assignment Counsel dropdown. */
export function isAssignableCounselUser(roles: string): boolean {
  return isCounselRole(roles) || isAllocationOfficerRole(roles);
}

export function filterAssignableCounselUsers<T extends { userRoles: string }>(users: T[]): T[] {
  const hasRoleData = users.some((u) => u.userRoles.trim());
  if (!hasRoleData) return users;
  return users.filter((u) => isAssignableCounselUser(u.userRoles));
}

export function formatCategoryAllocationUserLabel(user: {
  userDisplayName: string;
  userId: string;
  userRoles?: string;
}): string {
  const name = user.userDisplayName?.trim() || user.userId;
  const roles = user.userRoles?.trim() ?? '';
  if (name && roles) return `${name} - ${roles}`;
  return name;
}
